import React, {
  type ComponentProps,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { ModalContext } from './context';
import { type ModalName, modals } from './modals';

export const ModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [modalState, setModalState] = useState<
    {
      name: ModalName;
      props: ComponentProps<(typeof modals)[ModalName]>;
    }[]
  >([]);

  const openModal = useCallback(
    <T extends ModalName>(
      name: T,
      // eslint-disable-next-line @typescript-eslint/no-empty-object-type
      ...[props]: React.ComponentProps<(typeof modals)[T]> extends {}
        ? [props: React.ComponentProps<(typeof modals)[T]>]
        : []
    ) => {
      const modal = modals[name];

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!modal) {
        console.warn(`Modal with name "${name}" does not exist.`);

        return;
      }

      setModalState((prev) => [...prev, { name, props }]);
    },
    [],
  );

  const closeModal = useCallback((name: ModalName) => {
    setModalState((prev) => prev.filter((modal) => modal.name !== name));
  }, []);

  const closeAllModals = useCallback(() => {
    setModalState([]);
  }, []);

  const contextValue = useMemo(
    () => ({
      openModal,
      closeModal,
      closeAllModals,
    }),
    [openModal, closeModal, closeAllModals],
  );

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      {modalState.map(({ name, props }, index) => {
        const ModalComponent = modals[name];

        // @ts-expect-error Test Id is built inside of each modal
        return <ModalComponent key={`${name}-${index}`} {...(props ?? {})} />;
      })}
    </ModalContext.Provider>
  );
};
